import { api } from "../services/api.js";

// Shared tenant-resolution helpers for module surfaces mounted under
// /partner-admin (via cos-partner-admin-page.ts). That container passes
// only Circulate-flavoured attributes to every section it hosts
// (api-server/token/keyword/guardlevel/...) — `guardlevel` is the
// CIRCULATE guard scale (1-4), not COS's own 1/2/3+ access-level scale —
// so a section never receives the caller's COS level as a prop and must
// resolve it itself, the way cos-media-page.ts / cos-chat.ts already do.
//
// THE RULE (Paul, 2026-08-16): every Partner-workspace module surface
// picks up its library from the shared Partner-nav pick
// (`legacyClientId()`), never from its own fetched partner/client list.
// A restricted (library/partner-tier) caller never sees a choice at all
// — their own tenant is always implied server-side. These helpers exist
// so a module that is COS-native (works in the COS `partners.id` space,
// not the legacy `clientsV2.id` space the nav picker stores) can still
// follow that rule without re-fetching a picker list of its own.

interface CurrentUser {
	level?: number;
	partner_id?: number | null;
}

/**
 * The caller's COS access level + own partner id (present only for a
 * restricted/partner-tier caller). Fails toward "restricted" on error —
 * never assume cross-tenant access just because `/auth/me` hiccuped.
 */
export async function currentUser(): Promise<CurrentUser> {
	try {
		return (await api.getMe()) as CurrentUser;
	} catch {
		return { level: 3 };
	}
}

/** Level 1-2 = unrestricted Communico staff (no implied tenant — they
 *  pick a library in the shared Partner-nav). Level 3+ = restricted
 *  library/partner staff (their own tenant is always implied). */
export function isUnrestrictedLevel(level: number | undefined): boolean {
	return typeof level === "number" && level >= 1 && level < 3;
}

/**
 * Crosswalks the shared Partner-nav client pick (a legacy `clientsV2` id)
 * to the matching COS `partners.id`, via the platform-agnostic
 * `GET /measure/context` endpoint. That endpoint is `RequireLevelScoped(3)`
 * with no Measure-product-entitlement gate, so it's reachable from any
 * authenticated staff session regardless of which product the caller is
 * actually using — it's exactly this lookup (`partners.client_id = ?`),
 * already product-agnostic, so it's reused here rather than duplicated.
 *
 * Returns 0 when nothing crosswalks (or nothing is picked). A caller
 * MUST treat 0 as "no library resolved" — never fall back to an
 * arbitrary partner.
 */
export async function crosswalkClientToPartnerId(clientId: number): Promise<number> {
	if (clientId <= 0) return 0;
	try {
		const ctx = (await api.getMeasureContext({ clientId })) as { partner_id?: number };
		return ctx.partner_id ?? 0;
	} catch {
		return 0;
	}
}
